import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mt-12" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-center w-[2rem]"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "text-muted" }
const _hoisted_5 = ["href"]
const _hoisted_6 = { key: 2 }
const _hoisted_7 = ["href"]
const _hoisted_8 = {
  key: 3,
  class: "flex flex-col gap-6"
}
const _hoisted_9 = { class: "flex items-center gap-4 text-sm text-muted" }
const _hoisted_10 = ["href"]

import type { AppBskyFeedDefs, AppBskyRichtextFacet, AppBskyEmbedImages, AppBskyEmbedExternal } from '@atproto/api'

type Facet = AppBskyRichtextFacet.Main
type ThreadViewPost = AppBskyFeedDefs.ThreadViewPost

interface CommentEmbed {
  type: 'images' | 'external'
  images?: AppBskyEmbedImages.ViewImage[]
  external?: AppBskyEmbedExternal.ViewExternal
}

interface Comment {
  uri: string
  cid: string
  author: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
  text: string
  facets?: Facet[]
  embed?: CommentEmbed
  createdAt: string
  likeCount: number
  replyCount: number
  repostCount: number
  replies: Comment[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BlueskyComments.client',
  props: {
    uri: {}
  },
  setup(__props: any) {

const props = __props

function isThreadViewPost (v: unknown): v is ThreadViewPost {
  return (
    typeof v === 'object'
    && v !== null
    && '$type' in v
    && v.$type === 'app.bsky.feed.defs#threadViewPost'
  )
}

function parseEmbed (embed: unknown): CommentEmbed | undefined {
  if (!embed || typeof embed !== 'object') return undefined

  const e = embed as Record<string, unknown>

  if (e.$type === 'app.bsky.embed.images#view' && Array.isArray(e.images)) {
    return {
      type: 'images',
      images: e.images as AppBskyEmbedImages.ViewImage[],
    }
  }

  if (e.$type === 'app.bsky.embed.external#view' && e.external) {
    return {
      type: 'external',
      external: e.external as AppBskyEmbedExternal.ViewExternal,
    }
  }

  return undefined
}

function parseThread (thread: ThreadViewPost): Comment | null {
  if (!isThreadViewPost(thread)) return null

  const post = thread.post
  const record = post.record as { text: string, createdAt: string, facets?: Facet[] }

  const replies: Comment[] = []
  if (thread.replies) {
    for (const reply of thread.replies) {
      if (isThreadViewPost(reply)) {
        const parsed = parseThread(reply)
        if (parsed) replies.push(parsed)
      }
    }
    // Sort replies by date ascending (oldest first)
    replies.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  }

  return {
    uri: post.uri,
    cid: post.cid,
    author: {
      did: post.author.did,
      handle: post.author.handle,
      displayName: post.author.displayName,
      avatar: post.author.avatar,
    },
    text: record.text as string,
    createdAt: record.createdAt as string,
    facets: record.facets,
    embed: parseEmbed(post.embed),
    likeCount: post.likeCount ?? 0,
    replyCount: post.replyCount ?? 0,
    repostCount: post.repostCount ?? 0,
    replies,
  }
}

const { data: thread, pending, error } = useFetch(() =>
  `https://public.api.bsky.app/xrpc/app.bsky.feed.getPostThread`, {
  query: { uri: props.uri, depth: 10 },
  key: props.uri,
  lazy: true,
  server: false,
  transform: (response: { thread: ThreadViewPost }) => parseThread(response.thread),
})

const postUrl = computed(() => {
  // Convert AT URI to bsky.app URL
  // at://did:plc:xxx/app.bsky.feed.post/rkey -> https://bsky.app/profile/did:plc:xxx/post/rkey
  const match = props.uri.match(/at:\/\/([^/]+)\/app\.bsky\.feed\.post\/(.+)/)
  if (match) {
    const [, did, rkey] = match
    return `https://bsky.app/profile/${did}/post/${rkey}`
  }
  return null
})

return (_ctx: any,_cache: any) => {
  const _component_BlueskyComment = _resolveComponent("BlueskyComment")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[3] || (_cache[3] = _createElementVNode("hr", { class: "block mx-auto my-8 content w-4 border-t-2 border-solid border-gray-300 dark:border-gray-700" }, null, -1)),
    _cache[4] || (_cache[4] = _createElementVNode("h2", { class: "text-xl mb-4" }, " Comments ", -1)),
    (_unref(pending))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
          _createElementVNode("span", {
            class: "h-6 w-6 i-svg-spinners:90-ring-with-bg",
            "aria-hidden": "true"
          }, null, -1),
          _createElementVNode("span", { class: "sr-only" }, "Loading comments", -1)
        ]))]))
      : (_unref(error))
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("p", _hoisted_4, [
              _cache[1] || (_cache[1] = _createTextVNode(" Could not load comments. ", -1)),
              (_unref(postUrl))
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: _unref(postUrl),
                    target: "_blank",
                    rel: "noopener",
                    class: "underline rounded f-ring"
                  }, " View on Bluesky ", 8, _hoisted_5))
                : _createCommentVNode("", true)
            ])
          ]))
        : (!_unref(thread) || _unref(thread).replies.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[2] || (_cache[2] = _createElementVNode("p", { class: "text-muted mb-4" }, " No comments yet. ", -1)),
              (_unref(postUrl))
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: _unref(postUrl),
                    target: "_blank",
                    rel: "noopener",
                    class: "inline-flex items-center gap-2 text-sm underline rounded f-ring"
                  }, " Reply on Bluesky ", 8, _hoisted_7))
                : _createCommentVNode("", true)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("span", null, _toDisplayString(_unref(thread).replyCount) + " " + _toDisplayString(_unref(thread).replyCount === 1 ? 'reply' : 'replies'), 1),
                _createElementVNode("span", null, _toDisplayString(_unref(thread).likeCount) + " " + _toDisplayString(_unref(thread).likeCount === 1 ? 'like' : 'likes'), 1),
                (_unref(postUrl))
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 0,
                      href: _unref(postUrl),
                      target: "_blank",
                      rel: "noopener",
                      class: "underline ml-auto rounded f-ring"
                    }, " Reply on Bluesky ", 8, _hoisted_10))
                  : _createCommentVNode("", true)
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(thread).replies, (reply) => {
                return (_openBlock(), _createBlock(_component_BlueskyComment, {
                  key: reply.uri,
                  comment: reply,
                  depth: 0
                }, null, 8, ["comment"]))
              }), 128))
            ]))
  ]))
}
}

})